import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { tenantAdminAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Switch } from '../components/ui/switch'
import { toast } from 'sonner'
import {
  Palette, Type, Globe, Settings, Users, Building2, TrendingUp, Shield,
  Save, Loader2, Eye, BarChart3, Coins, RefreshCw
} from 'lucide-react'

export default function TenantAdminPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth()
  const { setTenant } = useTenant()
  const [config, setConfig] = useState(null)
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [branding, setBranding] = useState({})
  const [features, setFeatures] = useState({})

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated || !isAdmin) { navigate('/'); return }
    loadData()
  }, [isAuthenticated, isAdmin, authLoading, navigate])

  const loadData = async () => {
    try {
      const [cfgRes, statsRes, usersRes] = await Promise.all([
        tenantAdminAPI.getConfig(),
        tenantAdminAPI.getStats(),
        tenantAdminAPI.listUsers(),
      ])
      setConfig(cfgRes.data)
      setBranding(cfgRes.data.branding || {})
      setFeatures(cfgRes.data.features || {})
      setStats(statsRes.data)
      setUsers(usersRes.data)
    } catch (err) {
      toast.error('Failed to load configuration')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const updateColor = useCallback((key, value) => {
    setBranding(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }))
  }, [])

  const updateFont = useCallback((key, value) => {
    setBranding(prev => ({
      ...prev,
      fonts: { ...prev.fonts, [key]: value }
    }))
  }, [])

  const saveBranding = async () => {
    setSaving(true)
    try {
      const res = await tenantAdminAPI.updateBranding(branding)
      setBranding(res.data.branding)
      setTenant(prev => prev ? { ...prev, branding: res.data.branding } : prev)
      toast.success('Branding updated successfully!')
    } catch (err) {
      toast.error('Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  const toggleFeature = async (key, enabled) => {
    const updated = { ...features, [key]: { ...features[key], enabled } }
    setFeatures(updated)
    try {
      await tenantAdminAPI.updateFeatures({ [key]: enabled })
      toast.success(`${key} ${enabled ? 'enabled' : 'disabled'}`)
    } catch {
      setFeatures(features)
      toast.error('Failed to update feature')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      </div>
    )
  }

  const featureList = [
    { key: 'marketplace', label: 'Marketplace', icon: Building2, description: 'Property listings and marketplace' },
    { key: 'tokenization', label: 'Tokenization', icon: Coins, description: 'Token creation and management' },
    { key: 'kyc', label: 'KYC Verification', icon: Shield, description: 'Know Your Customer verification' },
    { key: 'reporting', label: 'Reporting', icon: BarChart3, description: 'Analytics and reports' },
    { key: 'secondary_market', label: 'Secondary Market', icon: RefreshCw, description: 'Token trading marketplace' },
    { key: 'api_access', label: 'API Access', icon: Settings, description: 'REST API for integrations' },
  ]

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="tenant-admin-page">
      <Navbar />
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="text-3xl font-heading text-brand-secondary" data-testid="tenant-admin-title">
              Platform Configuration
            </h1>
            <p className="text-slate-500 mt-1">
              {config?.name} &middot; Plan: <span className="text-brand-primary font-semibold uppercase">{config?.plan}</span>
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Users', value: stats?.users_count || 0, icon: Users, limit: stats?.limits?.max_users },
              { label: 'Properties', value: stats?.properties_count || 0, icon: Building2, limit: stats?.limits?.max_properties },
              { label: 'Active Offerings', value: stats?.active_offerings || 0, icon: TrendingUp, limit: stats?.limits?.max_offerings },
              { label: 'Pending KYC', value: stats?.pending_kyc || 0, icon: Shield },
              { label: 'Total Raised', value: `$${(stats?.total_raised || 0).toLocaleString()}`, icon: Coins },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-slate-100 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 text-sm uppercase tracking-wider">{stat.label}</span>
                  <stat.icon className="w-5 h-5 text-brand-primary" />
                </div>
                <p className="text-2xl font-heading text-brand-secondary font-mono" data-testid={`tenant-stat-${i}`}>
                  {stat.value}
                </p>
                {stat.limit && stat.limit > 0 && (
                  <p className="text-xs text-slate-400 mt-1">Limit: {stat.limit === -1 ? 'Unlimited' : stat.limit}</p>
                )}
              </div>
            ))}
          </motion.div>

          {/* Configuration Tabs */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Tabs defaultValue="branding">
              <TabsList className="bg-white border border-slate-100 p-1 rounded-none">
                <TabsTrigger value="branding" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="branding-tab">
                  <Palette className="w-4 h-4 mr-2" /> Branding
                </TabsTrigger>
                <TabsTrigger value="features" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="features-tab">
                  <Settings className="w-4 h-4 mr-2" /> Features
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="users-tab">
                  <Users className="w-4 h-4 mr-2" /> Users
                </TabsTrigger>
              </TabsList>

              {/* Branding Tab */}
              <TabsContent value="branding" className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Settings Panel */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* General */}
                    <div className="bg-white border border-slate-100 p-6">
                      <h3 className="text-lg font-heading text-brand-secondary mb-4">General</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Platform Name</Label>
                          <Input value={branding.platform_name || ''} onChange={e => setBranding(prev => ({ ...prev, platform_name: e.target.value }))}
                            className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="platform-name-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Tagline</Label>
                          <Input value={branding.tagline || ''} onChange={e => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                            className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="tagline-input" />
                        </div>
                        <div className="col-span-full space-y-2">
                          <Label className="text-slate-600 font-semibold">Logo URL</Label>
                          <Input value={branding.logo_url || ''} onChange={e => setBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                            placeholder="https://..." className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="logo-url-input" />
                        </div>
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="bg-white border border-slate-100 p-6">
                      <h3 className="text-lg font-heading text-brand-secondary mb-4 flex items-center gap-2">
                        <Palette className="w-5 h-5 text-brand-primary" /> Colors
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                          { key: 'primary', label: 'Primary', desc: 'Buttons, accents, highlights' },
                          { key: 'secondary', label: 'Secondary', desc: 'Backgrounds, text' },
                          { key: 'accent', label: 'Accent', desc: 'Success states, yields' },
                        ].map(c => (
                          <div key={c.key} className="space-y-2">
                            <Label className="text-slate-600 font-semibold">{c.label}</Label>
                            <div className="flex items-center gap-3">
                              <input type="color" value={branding.colors?.[c.key] || '#000000'}
                                onChange={e => updateColor(c.key, e.target.value)}
                                className="w-12 h-12 rounded-none border border-slate-200 cursor-pointer" data-testid={`color-${c.key}`} />
                              <div>
                                <Input value={branding.colors?.[c.key] || ''} onChange={e => updateColor(c.key, e.target.value)}
                                  className="h-10 w-28 bg-white border-slate-200 text-brand-secondary font-mono text-sm rounded-none" />
                                <p className="text-xs text-slate-400 mt-1">{c.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Typography */}
                    <div className="bg-white border border-slate-100 p-6">
                      <h3 className="text-lg font-heading text-brand-secondary mb-4 flex items-center gap-2">
                        <Type className="w-5 h-5 text-brand-primary" /> Typography
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Heading Font</Label>
                          <Input value={branding.fonts?.heading || ''} onChange={e => updateFont('heading', e.target.value)}
                            placeholder="Playfair Display" className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="font-heading-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Body Font</Label>
                          <Input value={branding.fonts?.body || ''} onChange={e => updateFont('body', e.target.value)}
                            placeholder="Manrope" className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="font-body-input" />
                        </div>
                      </div>
                    </div>

                    {/* Localization */}
                    <div className="bg-white border border-slate-100 p-6">
                      <h3 className="text-lg font-heading text-brand-secondary mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-brand-primary" /> Localization
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Default Language</Label>
                          <Input value={branding.default_language || 'en'} onChange={e => setBranding(prev => ({ ...prev, default_language: e.target.value }))}
                            className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="default-lang-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-600 font-semibold">Default Currency</Label>
                          <Input value={branding.default_currency || 'USD'} onChange={e => setBranding(prev => ({ ...prev, default_currency: e.target.value }))}
                            className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="default-currency-input" />
                        </div>
                      </div>
                    </div>

                    <Button onClick={saveBranding} disabled={saving}
                      className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="save-branding-btn">
                      {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                      Save Changes
                    </Button>
                  </div>

                  {/* Live Preview */}
                  <div className="lg:col-span-1">
                    <div className="sticky top-24 bg-white border border-slate-100 overflow-hidden">
                      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-brand-primary" />
                        <span className="text-sm font-semibold text-brand-secondary">Live Preview</span>
                      </div>
                      {/* Mini preview of branding */}
                      <div style={{ backgroundColor: branding.colors?.secondary || '#0F172A' }} className="p-6" data-testid="branding-preview">
                        <div className="flex items-center gap-3 mb-4">
                          {branding.logo_url && (
                            <img src={branding.logo_url} alt="Logo" className="h-8 w-auto" />
                          )}
                          <span className="text-white font-semibold">{branding.platform_name || 'Platform'}</span>
                        </div>
                        <p className="text-white/60 text-xs mb-4">{branding.tagline || 'Tagline'}</p>
                        <div style={{ backgroundColor: branding.colors?.primary || '#D4AF37' }}
                          className="px-4 py-2 text-center text-sm font-semibold" 
                          data-testid="preview-cta">
                          <span style={{ color: branding.colors?.secondary || '#0F172A' }}>CTA Button</span>
                        </div>
                      </div>
                      <div className="p-4 bg-[#F8FAFC]">
                        <div className="flex items-center gap-2 mb-2">
                          <div style={{ backgroundColor: branding.colors?.primary || '#D4AF37' }} className="w-4 h-4" />
                          <span className="text-xs text-slate-500">Primary</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <div style={{ backgroundColor: branding.colors?.secondary || '#0F172A' }} className="w-4 h-4" />
                          <span className="text-xs text-slate-500">Secondary</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div style={{ backgroundColor: branding.colors?.accent || '#10B981' }} className="w-4 h-4" />
                          <span className="text-xs text-slate-500">Accent</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Features Tab */}
              <TabsContent value="features" className="mt-6">
                <div className="bg-white border border-slate-100">
                  <div className="p-5 border-b border-slate-100">
                    <h3 className="text-lg font-heading text-brand-secondary">Feature Flags</h3>
                    <p className="text-sm text-slate-500 mt-1">Enable or disable features for your platform</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {featureList.map(feat => (
                      <div key={feat.key} className="p-5 flex items-center justify-between" data-testid={`feature-${feat.key}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-brand-primary/10 flex items-center justify-center">
                            <feat.icon className="w-5 h-5 text-brand-primary" />
                          </div>
                          <div>
                            <p className="text-brand-secondary font-semibold">{feat.label}</p>
                            <p className="text-sm text-slate-500">{feat.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={features[feat.key]?.enabled || false}
                          onCheckedChange={(checked) => toggleFeature(feat.key, checked)}
                          data-testid={`toggle-${feat.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users" className="mt-6">
                <div className="bg-white border border-slate-100 overflow-hidden">
                  <div className="p-5 border-b border-slate-100">
                    <h3 className="text-lg font-heading text-brand-secondary">Platform Users</h3>
                    <p className="text-sm text-slate-500 mt-1">{users.length} users on this tenant</p>
                  </div>
                  <table className="w-full">
                    <thead className="bg-[#F8FAFC]">
                      <tr>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">User</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Role</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">KYC</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {users.map(u => (
                        <tr key={u.id} className="hover:bg-[#F8FAFC]" data-testid={`tenant-user-${u.id}`}>
                          <td className="p-4">
                            <p className="text-brand-secondary font-semibold">{u.name}</p>
                            <p className="text-sm text-slate-400">{u.email || 'Wallet user'}</p>
                          </td>
                          <td className="p-4">
                            <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                              u.role === 'admin' ? 'bg-red-100 text-red-700' : u.role === 'issuer' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>{u.role}</span>
                          </td>
                          <td className="p-4">
                            <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                              u.kyc_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : u.kyc_status === 'in_review' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                            }`}>{u.kyc_status}</span>
                          </td>
                          <td className="p-4 text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </main>
    </div>
  )
}
